import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROL = { admin: "Administrador", coordinador: "Coordinador", vendedor: "Vendedor", granos: "Granos", analista: "Analista" };

export default function PermisosUsuarios() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [den, setDen] = useState([]);
  const [q, setQ] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async (mantener) => {
    try {
      const r = (await api.get("/superadmin/permisos", { params: { _t: Date.now() } })).data;
      setD(r);
      if (mantener) {
        const u = r.usuarios.find((x) => x.email === mantener);
        if (u) setDen(u.denegados);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron cargar los permisos"); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const usuarios = useMemo(() => (d?.usuarios || []).filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())), [d, q]);
  const actual = d?.usuarios.find((u) => u.email === sel);
  const elegir = (u) => { setSel(u.email); setDen(u.denegados); };
  const cambiado = actual && JSON.stringify([...den].sort()) !== JSON.stringify([...actual.denegados].sort());
  const alternar = (k) => setDen(den.includes(k) ? den.filter((x) => x !== k) : [...den, k]);
  const { refrescar } = useAuth();
  const [diag, setDiag] = useState(null);
  const diagnosticar = async () => {
    try { const r = await api.get(`/superadmin/permisos/${encodeURIComponent(sel)}/diagnostico`); setDiag(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo diagnosticar"); }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/superadmin/permisos/${encodeURIComponent(sel)}`, { denegados: den });
      toast.success("Permisos guardados. Aplican de inmediato (el servidor los lee de la base en cada pedido).");
      await cargar(sel);
      refrescar?.();
      setDiag(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setGuardando(false); }
  };
  const restablecer = async () => {
    if (!window.confirm("¿Volver a los permisos iniciales de este usuario?")) return;
    try { await api.delete(`/superadmin/permisos/${encodeURIComponent(sel)}`); toast.success("Restablecido"); await cargar(sel); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo restablecer"); }
  };

  if (!d) return <p className="text-sm text-slate-400">Cargando…</p>;
  const grupo = (g) => d.catalogo.filter((c) => c.grupo === g);
  const Caja = ({ c }) => (
    <label className="flex items-center gap-2 text-sm text-slate-700 py-1">
      <input type="checkbox" checked={!den.includes(c.key)} onChange={() => alternar(c.key)} disabled={!!actual?.superadmin} />
      {c.label}
    </label>
  );
  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4" data-testid="permisos-usuarios">
      <div className="space-y-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario…" className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm" />
        <ul className="max-h-[28rem] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {usuarios.map((u) => (
            <li key={u.email}>
              <button onClick={() => elegir(u)} className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 ${sel === u.email ? "bg-emerald-50" : ""}`}>
                <div className="font-medium text-slate-800 truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-slate-400 truncate">{u.email} · {ROL[u.role] || u.role}</div>
                {(u.denegados.length > 0 || !u.activo) && <div className="text-[10px] text-amber-700">{!u.activo ? "Desactivado · " : ""}{u.denegados.length > 0 ? `${u.denegados.length} restricción(es)` : ""}</div>}
              </button>
            </li>))}
        </ul>
      </div>
      <div>
        {!actual ? <p className="text-sm text-slate-400">Elegí un usuario de la lista para ver y cambiar lo que puede hacer.</p> : (
          <div className="space-y-4">
            <div>
              <div className="font-semibold text-emerald-950">{actual.name || actual.email}</div>
              <div className="text-xs text-slate-400">{actual.email} · {ROL[actual.role] || actual.role}{actual.personalizado ? " · permisos personalizados" : " · permisos iniciales"}</div>
              {actual.superadmin && <div className="text-xs text-amber-700 mt-1">Es superadministrador: no se puede restringir.</div>}
              <button type="button" onClick={diagnosticar} className="mt-1 text-xs text-emerald-700 underline" data-testid="permisos-diagnostico-btn">Diagnóstico: ¿puede guardar informes?</button>
              {diag && <div className={`mt-1 rounded border px-2 py-1 text-xs ${diag.puede_guardar ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="permisos-diagnostico">
                <b>{diag.puede_guardar ? "Puede guardar informes." : "Hoy NO puede guardar informes."}</b> {diag.lineas.join(" ")}
              </div>}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Qué puede ver</div>
                {grupo("ver").map((c) => <Caja key={c.key} c={c} />)}
              </div>
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Qué puede hacer</div>
                {grupo("hacer").map((c) => <Caja key={c.key} c={c} />)}
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Tildado = puede. Destildado = no lo ve o no puede hacerlo. Solo se puede quitar lo que su rol ya permite: tildar no le da más poder que el de su rol.</p>
            <div className="flex gap-3 items-center">
              <button onClick={guardar} disabled={!cambiado || guardando || actual.superadmin} className="bg-emerald-700 disabled:opacity-40 text-white text-sm px-4 py-1.5 rounded-md">Guardar cambios</button>
              {actual.personalizado && <button onClick={restablecer} className="text-xs text-slate-500 underline">Volver a los permisos iniciales</button>}
            </div>
          </div>)}
      </div>
    </div>
  );
}
